// Function to convert MIME types to user-friendly names
export const getFriendlyFileType = (mimeType, fileName) => {
  if (!mimeType) {
    // Fallback to file extension if no MIME type
    if (fileName) {
      const extension = fileName.split(".").pop()?.toLowerCase();
      const extensionMap = {
        pdf: "PDF Document",
        doc: "Word Document",
        docx: "Word Document",
        xls: "Excel Spreadsheet",
        ppt: "PowerPoint Presentation",
        pptx: "PowerPoint Presentation",
        txt: "Text File",
        md: "Markdown File",
        json: "JSON File",
        csv: "CSV File",
        html: "HTML File",
        css: "CSS File",
        js: "JavaScript File",
        ts: "TypeScript File",
        py: "Python File",
        java: "Java File",
        cpp: "C++ File",
        c: "C File",
        php: "PHP File",
        rb: "Ruby File",
        zip: "ZIP Archive",
        rar: "RAR Archive",
        png: "PNG Image",
        jpg: "JPEG Image",
        jpeg: "JPEG Image",
        gif: "GIF Image",
        webp: "WebP Image",
        svg: "SVG Image",
        mp4: "MP4 Video",
        mp3: "MP3 Audio",
        wav: "WAV Audio",
      };
      return (
        extensionMap[extension] || `${extension?.toUpperCase()} File` || "File"
      );
    }
    return "File";
  }

  // MIME type to friendly name mapping
  const mimeTypeMap = {
    // Microsoft Office documents
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "Word Document",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      "Excel Spreadsheet",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      "PowerPoint Presentation",
    "application/msword": "Word Document",
    "application/vnd.ms-excel": "Excel Spreadsheet",
    "application/vnd.ms-powerpoint": "PowerPoint Presentation",

    // Google Workspace documents
    "application/vnd.google-apps.document": "Google Doc",
    "application/vnd.google-apps.spreadsheet": "Google Sheet",
    "application/vnd.google-apps.presentation": "Google Slides",
    "application/vnd.google-apps.script": "Google Apps Script",

    // Common document types
    "application/pdf": "PDF Document",
    "text/plain": "Text File",
    "text/markdown": "Markdown File",
    "text/csv": "CSV File",
    "application/json": "JSON File",
    "text/html": "HTML File",
    "text/css": "CSS File",
    "application/javascript": "JavaScript File",
    "text/javascript": "JavaScript File",
    "application/typescript": "TypeScript File",

    // Programming languages
    "text/x-python": "Python File",
    "application/x-python-code": "Python File",
    "text/x-java-source": "Java File",
    "text/x-c": "C File",
    "text/x-c++src": "C++ File",
    "application/x-php": "PHP File",
    "text/x-ruby": "Ruby File",
    "application/x-tex": "LaTeX File",

    // Images
    "image/png": "PNG Image",
    "image/jpeg": "JPEG Image",
    "image/gif": "GIF Image",
    "image/webp": "WebP Image",
    "image/svg+xml": "SVG Image",
    "image/bmp": "BMP Image",
    "image/tiff": "TIFF Image",

    // Archives
    "application/zip": "ZIP Archive",
    "application/x-rar-compressed": "RAR Archive",
    "application/x-7z-compressed": "7Z Archive",
    "application/gzip": "GZIP Archive",

    // Audio/Video
    "video/mp4": "MP4 Video",
    "video/avi": "AVI Video",
    "video/quicktime": "QuickTime Video",
    "audio/mpeg": "MP3 Audio",
    "audio/wav": "WAV Audio",
    "audio/ogg": "OGG Audio",

    // Other common types
    "application/xml": "XML File",
    "text/xml": "XML File",
    "application/rtf": "RTF Document",
    "application/octet-stream": "Binary File",
  };

  // Return mapped type or fallback
  const friendlyType = mimeTypeMap[mimeType.toLowerCase()];
  if (friendlyType) {
    return friendlyType;
  }

  // If no exact match, try to extract a meaningful name from the MIME type
  if (mimeType.startsWith("text/")) {
    const subtype = mimeType.split("/")[1];
    return `${subtype.charAt(0).toUpperCase() + subtype.slice(1)} File`;
  }

  if (mimeType.startsWith("image/")) {
    const subtype = mimeType.split("/")[1];
    return `${subtype.toUpperCase()} Image`;
  }

  if (mimeType.startsWith("video/")) {
    const subtype = mimeType.split("/")[1];
    return `${subtype.toUpperCase()} Video`;
  }

  if (mimeType.startsWith("audio/")) {
    const subtype = mimeType.split("/")[1];
    return `${subtype.toUpperCase()} Audio`;
  }

  if (mimeType.startsWith("application/")) {
    const subtype = mimeType.split("/")[1];
    return `${subtype.charAt(0).toUpperCase() + subtype.slice(1)} File`;
  }

  // Final fallback - just return "File"
  return "File";
};
